import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agentsApi, AuditScope } from '@/lib/agents';
import { AppShell } from '@/components/app/AppShell';
import { Dropdown } from '@/components/ui/Dropdown';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { modelTierLabel } from '@/lib/model-label';
import { SaveResultButton } from '@/components/app/SaveResultButton';
import { ResultFooter } from '@/components/app/ResultFooter';
import { OutputLanguageSelect } from '@/components/app/OutputLanguageSelect';
import { HelpHint } from '@/components/app/HelpHint';
import { ExampleChips } from '@/components/app/ExampleChips';
import { LanguageCode, defaultOutputLanguage } from '@/i18n/languages';
import { helpFor, pickLang } from '@/lib/agentHelp';

const SCOPES: AuditScope[] = ['jsox', 'sox', 'gdpr', 'appi', 'general'];

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [scope, setScope] = useState<AuditScope>('jsox');
  const [question, setQuestion] = useState('');
  const [language, setLanguage] = useState<LanguageCode>(defaultOutputLanguage(i18n.language));

  const run = useMutation({
    mutationFn: () => agentsApi.audit(wsId!, { scope, question, language }),
  });

  return (
    <AppShell
      title={t('audit_agent.title', { defaultValue: 'Audit' })}
      description={t('audit_agent.subtitle', { defaultValue: 'Ask compliance-grounded questions and surface evidence with policy references.' }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-5">
        <HelpHint page="audit" />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('audit_agent.scope', { defaultValue: 'Scope' })}</label>
            <div className="w-full">
              <Dropdown<AuditScope>
                value={scope}
                onChange={(next) => setScope(next)}
                items={SCOPES.map((s) => ({
                  value: s,
                  label: t(`audit_agent.scope.${s}`, { defaultValue: s.toUpperCase() }) as string,
                }))}
                ariaLabel={t('audit_agent.scope', { defaultValue: 'Scope' }) as string}
                size="md"
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('compose.language', { defaultValue: 'Output language' })}</label>
            <div className="w-full">
              <OutputLanguageSelect value={language} onChange={setLanguage} />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-900 mb-1">{t('audit_agent.question', { defaultValue: 'Question' })}</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                if (question.trim() && !run.isPending) run.mutate();
              }
            }}
            placeholder={`${pickLang(helpFor('audit')?.placeholder, i18n.language)} ${t('help.enter_hint', { defaultValue: '· ↩ to run, ⇧↩ for a new line.' })}`}
            rows={3}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </div>

        {!question.trim() && (
          <ExampleChips page="audit" groupKey={scope} onPick={setQuestion} />
        )}

        <div className="flex justify-end">
          <button
            onClick={() => run.mutate()}
            disabled={!question.trim() || run.isPending}
            className="btn-press px-4 py-2 text-sm disabled:opacity-40"
          >
            {run.isPending ? t('audit_agent.running', { defaultValue: 'Running…' }) : t('audit_agent.submit', { defaultValue: 'Run audit' })}
          </button>
        </div>

        {run.data && (
          <article className="card-paper p-5">
            <div className="text-xs text-ink-500 mb-2 flex items-center justify-between">
              <span>{run.data.scope.toUpperCase()} · {run.data.policyRefs.length} {t('audit_agent.policy_refs', { defaultValue: 'policy refs' })} · {run.data.evidenceRefs.length} {t('audit_agent.evidence_refs', { defaultValue: 'evidence refs' })}</span>
              <span>{modelTierLabel(run.data.model)} · {run.data.inputTokens + run.data.outputTokens} tok · ¢{run.data.costCents}</span>
            </div>
            <CitedMarkdown content={run.data.answer} wsId={wsId} />
            <ResultFooter wsId={wsId!} citedChunkIds={run.data.evidenceRefs} askSeed={question}>
              <SaveResultButton
                workspaceId={wsId!}
                kind="audit"
                title={question}
                output={run.data.answer}
                input={{ scope, question, language }}
                citedChunkIds={run.data.evidenceRefs}
                model={run.data.model}
                costCents={run.data.costCents}
              />
            </ResultFooter>
          </article>
        )}
      </div>
    </AppShell>
  );
}
